import React from 'react';
import { motion } from 'framer-motion';
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { 
  HelpCircle,
  CheckCircle2,
  ShieldAlert,
  Loader2
} from "lucide-react";
import { ParcelValidationErrors } from "@/hooks/useParcelValidation";
import { useParcelTypes } from "@/hooks/useParcelTypes";
import { getIconByName } from "@/lib/icon-utils";
import { getGradientClasses, getIconGradientClasses } from "@/lib/color-utils";

interface ParcelTypeSelectorProps {
  selectedType: string;
  onTypeChange: (type: string) => void;
  errors?: ParcelValidationErrors;
}

const ParcelTypeSelector: React.FC<ParcelTypeSelectorProps> = ({
  selectedType,
  onTypeChange,
  errors = {}
}) => {
  const { parcelTypes, loading, error } = useParcelTypes(false, false, false);

  if (loading) {
    return (
      <div className="space-y-3">
        <Label>Parcel Type *</Label>
        <div className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <Label className="text-destructive">Parcel Type *</Label>
        <div className="text-center text-destructive p-4">
          Failed to load parcel types. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Label className={errors.type ? "validation-error" : ""}>Parcel Type *</Label>
      
      <div className="relative">
        {/* Main parcel type cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {parcelTypes.map((option, index) => {
            const isSelected = selectedType === option.key;
            const Icon = getIconByName(option.icon_name);
            const gradientClasses = getGradientClasses(option.color_from, option.color_to);
            const iconGradientClasses = getIconGradientClasses(option.color_from, option.color_to);
            
            return (
              <motion.div
                key={option.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="h-full"
              >
                <motion.div
                  className={cn(
                    "relative overflow-hidden rounded-xl border-2 cursor-pointer transition-all duration-200 h-full flex flex-col",
                    "hover:shadow-lg",
                    isSelected 
                      ? "border-primary shadow-md ring-2 ring-primary/20" 
                      : errors.type
                      ? "border-red-300 hover:border-red-400"
                      : "border-border hover:border-primary/50"
                  )}
                  onClick={() => onTypeChange(option.key)}
                  layout
                >
                  {/* Background gradient */}
                  <div className={cn(
                    "absolute inset-0 bg-gradient-to-br opacity-5",
                    gradientClasses
                  )} />
                  
                  {/* Content */}
                  <div className="relative p-4 space-y-3 flex-1 flex flex-col">
                    {/* Icon and selection indicator */}
                    <div className="flex items-center justify-between">
                      <div className={cn(
                        "p-2 rounded-lg bg-gradient-to-br text-white shadow-sm",
                        iconGradientClasses
                      )}>
                        <Icon className="h-5 w-5" />
                      </div>
                      
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-primary"
                        >
                          <CheckCircle2 className="h-5 w-5" />
                        </motion.div>
                      )}
                    </div>
                    
                    {/* Title and description */}
                    <div className="flex-1 flex flex-col">
                      <h3 className="font-medium text-sm leading-tight">
                        {option.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed flex-1">
                        {option.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Selection overlay */}
                  {isSelected && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute inset-0 bg-primary/5 pointer-events-none"
                    />
                  )}
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Other option button - positioned in bottom right */}
        <div className="flex justify-end mt-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.2 }}
          >
            <Button
              variant={selectedType === "other" ? "default" : "outline"}
              size="sm"
              onClick={() => onTypeChange("other")}
              className={cn(
                "flex items-center gap-2 transition-all duration-200",
                selectedType === "other" && "bg-primary text-primary-foreground"
              )}
            >
              <HelpCircle className="h-4 w-4" />
              Other (Please Specify)
            </Button>
          </motion.div>
        </div>
      </div>
      
      {errors.type && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-1 validation-error text-sm"
        >
          <ShieldAlert className="h-3 w-3" />
          <span>{errors.type}</span>
        </motion.div>
      )}
    </div>
  );
};

export default ParcelTypeSelector;
